import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileSpreadsheet, AlertTriangle } from 'lucide-react';
import type { PersistedWizardState } from '@/hooks/useWizardPersistence';

const STEP_LABELS = [
  'Select Files',
  'Review Selection',
  'Extract Tests',
  'Map Testers',
];

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

interface DraftResumeCardProps {
  draft: PersistedWizardState;
  isStale: boolean;
  onResume: () => void;
  onDiscard: () => void;
}

export function DraftResumeCard({ draft, isStale, onResume, onDiscard }: DraftResumeCardProps) {
  const stepLabel = STEP_LABELS[draft.currentStep - 1] ?? `Step ${draft.currentStep}`;

  return (
    <Card className="animate-fade-in">
      <CardContent className="flex flex-col items-center text-center py-10 px-6 space-y-4">
        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
          <FileSpreadsheet className="h-7 w-7 text-primary" />
        </div>

        <div className="space-y-1">
          <h2 className="text-lg font-semibold font-display">Continue where you left off?</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            You have an unsaved project draft. You can resume or start fresh.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {draft.projectName && (
            <Badge variant="secondary" className="text-sm">
              {draft.projectName}
            </Badge>
          )}
          <Badge variant="outline" className="text-sm">
            Step {draft.currentStep} of 4 &mdash; {stepLabel}
          </Badge>
          <Badge variant="outline" className="text-sm text-muted-foreground">
            Saved {timeAgo(draft.savedAt)}
          </Badge>
        </div>

        {isStale && (
          <div className="flex items-center gap-2 text-sm text-warning">
            <AlertTriangle className="h-4 w-4" />
            <span>This draft is over 12 hours old</span>
          </div>
        )}

        {draft.needsReparse && (
          <p className="text-xs text-muted-foreground max-w-sm">
            Parse details were too large to save. You&apos;ll need to reparse on Step 3.
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={onDiscard}>
            Start Fresh
          </Button>
          <Button onClick={onResume}>
            Resume Draft
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
